import {Component, ElementRef, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {NbDialogService, NbPopoverDirective} from '@nebular/theme';
import moment from 'moment';
import {Subject} from 'rxjs';
import {InvoicingService} from '../shared/invoicing.service';
import {InvoiceListItem} from '../interfaces/invoicing';
import {InvoiceKind, invoiceKind} from '../invoice/invoice-status/invoice-status.component';
import {AuthService} from '../shared/auth.service';
import {UserStatus} from '../interfaces/user';
import {TranslateService} from '@ngx-translate/core';
import {NewInvoiceDialogComponent} from '../project/new-invoice-dialog/new-invoice-dialog.component';
import {SubscribedComponent} from '../subscribed.component';
import {DualAxisDelegate, DualAxisEvent} from '../dual-axis/dual-axis.component';
import {ColDef, ColType} from '../interfaces/colDef';
import {TreeNode} from '../interfaces/treenode';
import {TableOptions} from '../settings/table-options/table-options.component';
import {Paged} from '../interfaces/paged';

// What an invoice shows in the Status column, and the route segment of the tab that lists that status.
const TABS: {key: string, title: string, kind?: InvoiceKind}[] = [
  {key: 'all', title: 'invoice.tab.all'},
  {key: 'drafts', title: 'invoice.tab.drafts', kind: 'draft'},
  {key: 'final', title: 'invoice.tab.final', kind: 'final'},
  {key: 'credited', title: 'invoice.tab.credited', kind: 'credited'},
  {key: 'credit-notes', title: 'invoice.tab.credit_notes', kind: 'credit_note'},
];

// Client and project names are the only open-ended text. Their two columns share what the others leave
// (max-width 0 plus a percentage width) and end in an ellipsis, so a long name never pushes Total off the right edge.
const NAME_COL = {maxWidth: '0', minWidth: '120px', width: '50%', textOverflow: 'ellipsis'};

/** Every invoice of the tenant on the standard list shell (status tabs, bst-dual-axis), a new draft for any project,
 *  and for an Admin the final PDFs of a date range as one ZIP (#1518). The list loads once; tabs, sorting and paging run on it. */
@Component({
  selector: 'bst-invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss'],
  standalone: false
})
export class InvoicesComponent extends SubscribedComponent implements OnInit, DualAxisDelegate<InvoiceListItem> {
  @ViewChild('cellTemplate', {static: true, read: TemplateRef}) cellTemplateOutlet: TemplateRef<any>;
  @ViewChild('cardTemplate', {static: true, read: TemplateRef}) cardTemplateOutlet: TemplateRef<any>;

  invoices: InvoiceListItem[] = null;
  private loaded: Promise<InvoiceListItem[]>;
  busy = false;
  @ViewChild('rangefield') rangefield: ElementRef<HTMLInputElement>;
  @ViewChild('datePop') datePop: NbPopoverDirective;
  // A bookkeeper imports the month just closed; the ZIP holds the invoices dated in the range.
  readonly dates = new FormControl<{start: Date, end?: Date}>(InvoicesComponent.preset('last_month'));
  // The Schedules overview's date presets, with the closed periods a bookkeeper exports first.
  readonly presets = ['last_month', 'month', 'last_quarter', 'quarter', 'year', 'custom'];

  tabs: {title: string, route: string, responsive: boolean}[] = [];
  status = 'all';
  count: number = null;
  events = new Subject<DualAxisEvent>();
  data: TreeNode<InvoiceListItem>[] = [];
  stickyColumnsLeft: ColDef[] = [
    {name: 'number', title: 'invoice.number', type: ColType.STRING, required: true, filter: false, sort: true},
  ];
  standardColumns: ColDef[] = [
    // Status beside the number: the row reads as "which invoice, in what state" before the details.
    {name: 'status', title: 'invoice.status', type: ColType.STRING, required: false, filter: false, sort: true},
    {name: 'client', title: 'invoice.client', type: ColType.STRING, required: false, filter: false, sort: true, style: NAME_COL},
    {name: 'project', title: 'invoice.project', type: ColType.STRING, required: false, filter: false, sort: true, style: NAME_COL},
    {name: 'period', title: 'invoice.period', type: ColType.DATE, required: false, filter: false, sort: true},
    {name: 'invoice_date', title: 'invoice.invoice_date', type: ColType.DATE, required: false, filter: false, sort: true},
    {name: 'due_date', title: 'invoice.due_date', type: ColType.DATE, required: false, filter: false, sort: true},
    // Amounts line up on the right, header included (bst-dual-axis gives the header the column's text-align).
    {name: 'total', title: 'invoice.total_incl', type: ColType.CURRENCY, required: false, filter: false, sort: true, style: {'text-align': 'right'}},
  ];
  stickyColumnsRight: ColDef[] = [];
  options: TableOptions = {
    display: [...this.stickyColumnsLeft, ...this.standardColumns].map(c => ({col: c.name, shown: true})),
    others: {}, filter: {}, sorts: [],
  };

  constructor(private invoicing: InvoicingService, private auth: AuthService, private router: Router, private translate: TranslateService,
              private dialogs: NbDialogService, private route: ActivatedRoute) {
    super();
  }

  get headerTitle() {
    return {title: 'invoice.invoices', translate: true};
  }

  // The UI language, passed to the currency pipe: LOCALE_ID is fixed at bootstrap.
  get lang(): string {
    return this.translate.currentLang ?? this.translate.defaultLang;
  }

  ngOnInit() {
    super.ngOnInit();
    this.loaded = this.invoicing.getAllInvoices().catch(() => []).then(list => this.invoices = list);
    // nb-route-tabset shows titles as given, so they follow the language here.
    this.subs.push(this.translate.stream(TABS.map(t => t.title)).subscribe(titles =>
      this.tabs = TABS.map(t => ({title: titles[t.title], route: `/invoices/view/${t.key}`, responsive: true}))));
    // The first emission comes before bst-dual-axis bootstraps, so its RESET is a no-op and the
    // first load already has the tab; later tab clicks reload through RESET.
    this.subs.push(this.route.paramMap.subscribe(params => {
      const status = params.get('status');
      if (!TABS.some(t => t.key === status)) {
        void this.router.navigate(['/invoices/view/all'], {replaceUrl: true});
        return;
      }
      this.status = status;
      this.events.next(DualAxisEvent.RESET);
    }));
  }

  private sortValue(invoice: InvoiceListItem, col: string): string | number {
    switch (col) {
      case 'number': return invoice.number ?? '';
      case 'client': return invoice.client_name ?? '';
      case 'project': return invoice.project?.name ?? '';
      case 'period': return invoice.period_from ?? '';
      case 'status': return this.translate.instant('invoice.' + invoiceKind(invoice));
      case 'total': return invoice.gross_cents ?? -Infinity;
      default: return invoice[col] ?? '';
    }
  }

  async paged(page: number, pageSize: number): Promise<Paged<InvoiceListItem>> {
    this.loading.next(true);
    try {
      const kind = TABS.find(t => t.key === this.status)?.kind;
      const rows = (await this.loaded).filter(i => !kind || invoiceKind(i) === kind);
      const sort = this.options.sorts?.[0];
      if (sort) {
        const dir = sort.dir === 2 ? -1 : 1;
        rows.sort((a, b) => {
          const x = this.sortValue(a, sort.col), y = this.sortValue(b, sort.col);
          return dir * (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), this.lang));
        });
      }
      const from = (page - 1) * pageSize;
      const slice = rows.slice(from, from + pageSize);
      this.count = rows.length;
      this.data = slice.map(i => ({data: i, expanded: false, children: []}));
      return {current_page: page, data: slice, from: slice.length ? from + 1 : 0, to: from + slice.length,
        last_page: Math.max(1, Math.ceil(rows.length / pageSize)), per_page: pageSize, total: rows.length};
    } finally {
      this.loading.next(false);
    }
  }

  selectRow(_event: MouseEvent, node: TreeNode<InvoiceListItem>) {
    this.router.navigate(['/invoices', node.data.id]);
  }

  get admin(): boolean {
    return this.auth.isAdmin && this.auth.user.getValue()?.user_to_client?.status === UserStatus.ACTIVE;
  }

  // The sidebar's check for the Invoices entry; the server decides per project.
  get canCreate(): boolean {
    return this.auth.canInvoice && !!this.auth.user.getValue()?.invoicing_enabled;
  }

  // Every final invoice whatever the tab: the ZIP does not follow the list filter.
  get finals(): InvoiceListItem[] {
    return (this.invoices ?? []).filter(i => i.status === 'final');
  }

  static preset(preset: string): {start: Date, end: Date} {
    const [unit, back] = preset.startsWith('last_') ? [preset.slice(5), 1] : [preset, 0];
    const at = moment().subtract(back, unit as moment.unitOfTime.DurationConstructor);
    return {start: at.clone().startOf(unit as moment.unitOfTime.StartOf).toDate(), end: at.clone().endOf(unit as moment.unitOfTime.StartOf).toDate()};
  }

  applyPreset(preset: string): void {
    this.datePop?.hide();
    preset === 'custom' ? this.rangefield?.nativeElement.focus() : this.dates.setValue(InvoicesComponent.preset(preset));
  }

  // The invoice dates the ZIP covers, both ends included; none while the picker has only a start.
  get range(): [string, string] {
    const {start, end} = this.dates.value ?? {} as any;
    return start && end ? [moment(start).format('YYYY-MM-DD'), moment(end).format('YYYY-MM-DD')] : null;
  }

  get inRange(): InvoiceListItem[] {
    const range = this.range;
    return range ? this.finals.filter(i => i.invoice_date >= range[0] && i.invoice_date <= range[1]) : [];
  }

  // The server refuses the ZIP while any final invoice in the range has no PDF.
  get pending(): string {
    return this.inRange.filter(i => !i.pdf_stored).map(i => i.number).join(', ');
  }

  async downloadAll() {
    this.busy = true;
    await this.invoicing.downloadAll(...this.range).catch(() => null);
    this.busy = false;
  }

  // Without a project the dialog asks for one.
  create() {
    this.dialogs.open(NewInvoiceDialogComponent);
  }
}
